/**
 * 视频工具（视频消息发送与视频快捷回复/自动回复共用）
 */

/**
 * 视频文件识别：扩展名判定（与 ChatInput.isVideoFile 现有口径及后端上传白名单一致，
 * 不以 MIME 前缀判定，避免空 type/白名单外格式误入视频链路）。
 * @param {File} file
 * @returns {boolean}
 */
export function isVideoFile(file) {
    return /\.(mp4|mov|avi|mkv|webm|flv|rmvb)$/i.test((file && file.name) || '');
}

/**
 * 读取视频元数据（时长/宽高）并截帧生成封面 blob；解码失败/超时降级返回空封面。
 * @param {File} file - 视频文件
 * @returns {Promise<{duration: number, width: number, height: number, snapshotBlob: Blob|null}>}
 */
export function readVideoMeta(file) {
    return new Promise((resolve) => {
        let url = URL.createObjectURL(file);
        let video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.src = url;
        let settled = false;
        let finish = (duration, width, height, snapshotBlob) => {
            if (settled) {
                return;
            }
            settled = true;
            URL.revokeObjectURL(url);
            resolve({ duration, width, height, snapshotBlob });
        };
        let fallback = (duration) => finish(duration, 0, 0, null);
        // 解码/seek 异常兜底：5 秒未完成则放弃封面（部分编码 seek 不触发 onseeked，避免 Promise 挂起）
        let timer = setTimeout(() => fallback(0), 5000);
        video.onerror = () => {
            clearTimeout(timer);
            fallback(0);
        };
        video.onloadedmetadata = () => {
            let duration = isFinite(video.duration) ? Math.round(video.duration) : 0;
            let width = video.videoWidth || 0;
            let height = video.videoHeight || 0;
            // 无法解码画面时仅返回时长
            if (!width || !height) {
                clearTimeout(timer);
                fallback(duration);
                return;
            }
            // 截帧：seek 到中段（短视频取 0.1s）
            video.onseeked = () => {
                try {
                    let canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(video, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        clearTimeout(timer);
                        finish(duration, width, height, blob);
                    }, 'image/jpeg', 0.7);
                } catch (e) {
                    clearTimeout(timer);
                    fallback(duration);
                }
            };
            video.currentTime = Math.min(duration / 2, 0.5) || 0.1;
        };
    });
}

/**
 * 格式化视频时长（秒 → mm:ss，与聊天区视频消息展示口径一致）
 * @param {number} seconds
 * @returns {string}
 */
export function formatVideoDuration(seconds) {
    seconds = parseInt(seconds) || 0;
    let m = Math.floor(seconds / 60);
    let s = seconds % 60;
    return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
}
